import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { PROJECTS } from "@/constants/testIds";
import { Reveal } from "@/components/Reveal";

export default function Progetti() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const contexts = useMemo(() => {
    const s = new Set(projects.map((p) => p.context).filter(Boolean));
    return ["all", ...Array.from(s)];
  }, [projects]);

  const filtered = filter === "all" ? projects : projects.filter((p) => p.context === filter);

  return (
    <div className="bg-cream grain pt-32 pb-24">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-3">
            <p className="text-xs uppercase tracking-[0.3em] text-ink/60">— Progetti</p>
          </div>
          <div className="col-span-12 md:col-span-9">
            <Reveal>
              <h1 className="font-heading text-6xl md:text-[9rem] leading-[0.85] tracking-tighter">
                Indice<br /><span className="italic text-peach">progetti.</span>
              </h1>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mt-20">
        <div className="flex flex-wrap gap-3">
          {contexts.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={c === "all" ? PROJECTS.filterAll : PROJECTS.filterContext(c)}
              className={`rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.22em] border transition-all ${
                filter === c
                  ? "bg-ink text-cream border-ink"
                  : "border-line-dark text-ink/70 hover:border-peach hover:text-peach"
              }`}
            >
              {c === "all" ? "Tutti" : c}
            </button>
          ))}
        </div>
      </section>

      <section
        data-testid={PROJECTS.list}
        className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mt-12 border-t border-line"
      >
        {filtered.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.06}>
            <Link
              to={`/progetti/${p.slug}`}
              data-testid={PROJECTS.card(p.slug)}
              className="group grid grid-cols-12 gap-6 py-12 border-b border-line hover:bg-cream-alt/40 transition-colors px-2 -mx-2"
            >
              <div className="col-span-2 md:col-span-1">
                <span className="num-index text-4xl md:text-6xl text-line-dark group-hover:text-peach transition-colors">
                  {String(p.index).padStart(2, "0")}
                </span>
              </div>
              <div className="col-span-10 md:col-span-5">
                <h2 className="font-heading text-4xl md:text-6xl leading-[0.95] tracking-tighter text-ink group-hover:translate-x-4 transition-transform duration-700">
                  {p.title}
                </h2>
                <p className="text-xs uppercase tracking-[0.22em] text-ink/50 mt-3">
                  {p.context} · {p.year}
                </p>
              </div>
              <div className="col-span-12 md:col-span-5">
                <p className="font-heading italic text-xl md:text-2xl text-ink/80 leading-snug">
                  {p.tagline}
                </p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-xs uppercase tracking-widest text-ink/40">
                  {p.tags.slice(0, 4).map((t) => <span key={t}>· {t}</span>)}
                </div>
              </div>
              <div className="hidden md:flex md:col-span-1 justify-end items-center">
                <ArrowUpRight
                  size={32}
                  className="text-ink/30 group-hover:text-peach group-hover:rotate-45 transition-all duration-500"
                />
              </div>
            </Link>
          </Reveal>
        ))}
        {filtered.length === 0 && (
          <p className="py-16 text-center text-ink/50 font-heading text-2xl italic">
            Nessun progetto in questa categoria.
          </p>
        )}
      </section>
    </div>
  );
}
